import { buildCalendarEventCatalog } from '../../calendar/utils/calendar.catalog';
import { getStoredSessionToken } from '../../../app/services/session.service';

const ACCOUNTING_API_BASE_URL = (process.env.REACT_APP_ACCOUNTING_API_BASE_URL || '').replace(
  /\/$/,
  ''
);
const DEALS_API_BASE_URL = (process.env.REACT_APP_DEALS_API_BASE_URL || '').replace(/\/$/, '');
const STAFF_API_BASE_URL = (process.env.REACT_APP_STAFF_API_BASE_URL || '').replace(/\/$/, '');
const CLIENTS_API_BASE_URL = (process.env.REACT_APP_CLIENTS_API_BASE_URL || '').replace(/\/$/, '');
const PAYMENTS_API_PATH = '/api/accounting/payments';
const EXPENSES_API_PATH = '/api/accounting/expenses';
const PROPOSALS_API_PATH = '/api/deals/proposals';
const IN_PROGRESS_API_PATH = '/api/deals/in-progress';
const COMPLETED_API_PATH = '/api/deals/completed';
const ASSIGNMENTS_API_PATH = '/api/deals/assignments';
const STAFF_API_PATH = '/api/staff';
const CLIENTS_API_PATH = '/api/clients';
const TENANT_DB_ERROR_CODES = new Set([
  'tenant_database_not_ready',
  'tenant_database_unavailable',
  'tenant_database_metadata_missing',
]);

function getAccountingApiUrl(path) {
  return `${ACCOUNTING_API_BASE_URL}${path}`;
}

function getDealsApiUrl(path) {
  return `${DEALS_API_BASE_URL}${path}`;
}

function getStaffApiUrl(path) {
  return `${STAFF_API_BASE_URL}${path}`;
}

function getClientsApiUrl(path) {
  return `${CLIENTS_API_BASE_URL}${path}`;
}

function isAuthFailure(status) {
  return status === 401 || status === 403;
}

async function readErrorPayload(response) {
  try {
    const responseToParse = typeof response.clone === 'function' ? response.clone() : response;
    return await responseToParse.json();
  } catch {
    return null;
  }
}

function mapTenantDatabaseDashboardError(payload, prefix) {
  if (!TENANT_DB_ERROR_CODES.has(payload?.error)) {
    return null;
  }

  if (payload.error === 'tenant_database_not_ready') {
    return `${prefix}_TENANT_DATABASE_NOT_READY`;
  }

  if (payload.error === 'tenant_database_metadata_missing') {
    return `${prefix}_TENANT_DATABASE_METADATA_MISSING`;
  }

  return `${prefix}_TENANT_DATABASE_UNAVAILABLE`;
}

async function resolveDashboardFailure(response, { authError, moduleDisabledError, tenantDatabasePrefix }) {
  if (response.ok) {
    return null;
  }

  const payload = await readErrorPayload(response);
  if (payload?.error === 'module_disabled') {
    return moduleDisabledError;
  }

  const tenantDatabaseError = mapTenantDatabaseDashboardError(payload, tenantDatabasePrefix);
  if (tenantDatabaseError) {
    return tenantDatabaseError;
  }

  if (isAuthFailure(response.status)) {
    return authError;
  }

  return null;
}

async function throwFirstDashboardFailure(responses, options) {
  const failures = await Promise.all(
    responses.map((response) => resolveDashboardFailure(response, options))
  );
  const authFailure = failures.find((failure) => failure === options.authError);
  const failure = authFailure || failures.find(Boolean);

  if (failure) {
    throw new Error(failure);
  }
}

function getDashboardAuthHeaders(extraHeaders = {}) {
  const token = getStoredSessionToken();

  if (!token) {
    return extraHeaders;
  }

  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`,
  };
}

function mapDashboardAssignment(item) {
  return {
    id: item.id,
    title: item.title || '',
    status: item.status || 'in_attesa',
    urgency: item.urgency || 'bassa',
    sender: item.sender || 'N/D',
    senderId: item.senderId ?? null,
    recipientIds: Array.isArray(item.recipientIds) ? item.recipientIds : [],
    recipients: Array.isArray(item.recipients) ? item.recipients : [],
    staffId: item.staffId ?? null,
    staffIds: Array.isArray(item.staffIds) ? item.staffIds : [],
    assigneeId: item.assigneeId ?? null,
    assigneeIds: Array.isArray(item.assigneeIds) ? item.assigneeIds : [],
    assignedTo: item.assignedTo ?? null,
    responsibleId: item.responsibleId ?? null,
    userId: item.userId ?? null,
    type: item.type || 'altro',
    createdAt: item.createdAt || null,
    dueDate: item.dueDate || null,
    raw: item,
  };
}

function mapDashboardStaff(item) {
  const name = String(item.name || item.displayName || item.fullName || '').trim();
  const fullName = String(item.fullName || item.displayName || item.name || '').trim();

  return {
    id: item.id,
    name,
    fullName,
    staffId: item.staffId ?? null,
    userId: item.userId ?? null,
    linkedWorkspaceUserId: item.linkedWorkspaceUserId ?? null,
    workspaceUserId: item.workspaceUserId ?? null,
    raw: item,
  };
}

function mapDashboardClient(item) {
  return {
    id: item.id,
    name: item.name || '',
    raw: item,
  };
}

function normalizeDashboardClientId(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  return String(value);
}

function getDashboardDealValue(deal) {
  const numericValue = Number(deal?.value ?? deal?.estimatedValue ?? 0);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

function resolveDashboardClientReference(clientMap, clientId, legacyClientName = '') {
  const normalizedClientId = normalizeDashboardClientId(clientId);
  const legacyName = String(legacyClientName || '').trim();

  if (!normalizedClientId) {
    return {
      clientResolvedName: legacyName || 'Cliente non risolto',
      clientResolutionStatus: legacyName ? 'legacy_fallback' : 'missing_client_id',
      clientLegacyName: legacyName,
    };
  }

  const catalogClient = clientMap.get(normalizedClientId);
  if (catalogClient?.name) {
    return {
      clientResolvedName: catalogClient.name,
      clientResolutionStatus: 'resolved',
      clientLegacyName: legacyName,
    };
  }

  return {
    clientResolvedName: legacyName || 'Cliente non risolto',
    clientResolutionStatus: 'unresolved_client_reference',
    clientLegacyName: legacyName,
  };
}

function enrichDashboardClientReferences(items = [], clientMap) {
  return items.map((item) => {
    if (!Object.prototype.hasOwnProperty.call(item || {}, 'clientId') && !item?.client) {
      return item;
    }

    const resolution = resolveDashboardClientReference(clientMap, item?.clientId, item?.client);

    return {
      ...item,
      ...resolution,
      client: resolution.clientResolvedName,
    };
  });
}

function ensureDashboardClientSummary(bucket, clientId, fallbackName = '') {
  const existing = bucket.get(clientId);
  if (existing) {
    if (!existing.fallbackName && fallbackName) {
      existing.fallbackName = fallbackName;
    }

    return existing;
  }

  const summary = {
    proposalCount: 0,
    inProgressCount: 0,
    completedCount: 0,
    totalValue: 0,
    fallbackName,
  };

  bucket.set(clientId, summary);
  return summary;
}

function collectProposalClientsById(deals = [], bucket) {
  deals.forEach((deal) => {
    const clientId = normalizeDashboardClientId(deal?.clientId);
    if (!clientId) {
      return;
    }

    const summary = ensureDashboardClientSummary(bucket, clientId, deal?.client || '');
    summary.proposalCount += 1;
  });
}

function collectInProgressClientsById(deals = [], bucket) {
  deals.forEach((deal) => {
    const clientId = normalizeDashboardClientId(deal?.clientId);
    if (!clientId) {
      return;
    }

    const summary = ensureDashboardClientSummary(bucket, clientId, deal?.client || '');
    summary.inProgressCount += 1;
    summary.totalValue += getDashboardDealValue(deal);
  });
}

function collectCompletedClientsById(deals = [], bucket) {
  deals.forEach((deal) => {
    const clientId = normalizeDashboardClientId(deal?.clientId);
    if (!clientId) {
      return;
    }

    const summary = ensureDashboardClientSummary(bucket, clientId, deal?.client || '');
    summary.completedCount += 1;
    summary.totalValue += getDashboardDealValue(deal);
  });
}

function buildDashboardClientCatalog(clients = []) {
  return new Map(
    clients
      .filter((client) => normalizeDashboardClientId(client?.id))
      .map((client) => [normalizeDashboardClientId(client.id), client])
  );
}

export function buildDashboardClientsSummary({
  clients = [],
  dealProposals = [],
  dealsInProgress = [],
  dealsCompleted = [],
}) {
  const clientMap = buildDashboardClientCatalog(clients);
  const clientsById = new Map();
  const unresolvedReferences = [];

  collectProposalClientsById(dealProposals, clientsById);
  collectInProgressClientsById(dealsInProgress, clientsById);
  collectCompletedClientsById(dealsCompleted, clientsById);

  [...dealProposals, ...dealsInProgress, ...dealsCompleted].forEach((deal) => {
    const resolution = resolveDashboardClientReference(clientMap, deal?.clientId, deal?.client);
    if (resolution.clientResolutionStatus !== 'resolved') {
      unresolvedReferences.push({
        clientId: normalizeDashboardClientId(deal?.clientId),
        ...resolution,
      });
    }
  });

  return {
    totalClients: clients.length,
    activeClients: Array.from(clientsById.values()).filter((summary) => summary.inProgressCount > 0)
      .length,
    potentialClients: Array.from(clientsById.values()).filter(
      (summary) =>
        summary.proposalCount > 0 &&
        summary.inProgressCount === 0 &&
        summary.completedCount === 0
    ).length,
    topClients: Array.from(clientsById.entries())
      .sort((left, right) => right[1].totalValue - left[1].totalValue)
      .slice(0, 3)
      .map(([clientId, summary]) => ({
        ...resolveDashboardClientReference(clientMap, clientId, summary.fallbackName),
        clientId,
        client: resolveDashboardClientReference(clientMap, clientId, summary.fallbackName)
          .clientResolvedName,
        value: summary.totalValue,
      })),
    unresolvedClientReferencesCount: unresolvedReferences.length,
    unresolvedClientReferences: unresolvedReferences,
  };
}

export function buildDashboardReadModel({
  accountingResult,
  dealsResult,
  assignmentsResult,
  clientsResult,
  calendarEventsResult,
}) {
  const clients = clientsResult?.ok ? clientsResult.data.clients : [];
  const clientMap = buildDashboardClientCatalog(clients);

  const readModel = {
    payments: accountingResult?.ok
      ? enrichDashboardClientReferences(accountingResult.data.payments, clientMap)
      : [],
    expenses: accountingResult?.ok ? accountingResult.data.expenses : [],
    accountingUnavailable: accountingResult?.ok === false,
    dealProposals: dealsResult?.ok
      ? enrichDashboardClientReferences(dealsResult.data.dealProposals, clientMap)
      : [],
    dealsInProgress: dealsResult?.ok
      ? enrichDashboardClientReferences(dealsResult.data.dealsInProgress, clientMap)
      : [],
    dealsCompleted: dealsResult?.ok
      ? enrichDashboardClientReferences(dealsResult.data.dealsCompleted, clientMap)
      : [],
    assignments: assignmentsResult?.ok ? assignmentsResult.data.assignments : [],
    staff: assignmentsResult?.ok ? assignmentsResult.data.staff : [],
    assignmentsUnavailable: assignmentsResult?.ok === false,
    assignmentsError: assignmentsResult?.ok === false ? assignmentsResult.error || '' : '',
    clients,
    calendarEvents: [],
    calendarEventsUnavailable: calendarEventsResult?.ok === false,
    companyAccounting: null,
    overdueUnavailableModules: [
      [assignmentsResult, 'Task'], [accountingResult, 'Incassi e uscite'], [dealsResult, 'Affari'], [calendarEventsResult, 'Calendario'],
    ].filter(([result]) => result?.ok === false && !String(result.error || '').endsWith('_MODULE_DISABLED')).map(([, label]) => label),
  };
  readModel.calendarEvents = buildCalendarEventCatalog({
    manualEvents: calendarEventsResult?.ok ? calendarEventsResult.data : [],
    assignments: readModel.assignments, payments: readModel.payments, expenses: readModel.expenses,
    proposals: readModel.dealProposals, inProgressDeals: readModel.dealsInProgress,
    completedDeals: readModel.dealsCompleted,
  });
  readModel.calendarEventsPartial = [accountingResult, dealsResult, assignmentsResult]
    .some((result) => result?.ok === false && !String(result.error || '').endsWith('_MODULE_DISABLED'));
  return readModel;
}

export async function getDashboardAccountingDataResult() {
  try {
    const [paymentsResponse, expensesResponse] = await Promise.all([
      fetch(getAccountingApiUrl(PAYMENTS_API_PATH), { headers: getDashboardAuthHeaders() }),
      fetch(getAccountingApiUrl(EXPENSES_API_PATH), { headers: getDashboardAuthHeaders() }),
    ]);

    await throwFirstDashboardFailure([paymentsResponse, expensesResponse], {
      authError: 'DASHBOARD_ACCOUNTING_AUTH_REQUIRED',
      moduleDisabledError: 'DASHBOARD_ACCOUNTING_MODULE_DISABLED',
      tenantDatabasePrefix: 'DASHBOARD_ACCOUNTING',
    });

    if (!paymentsResponse.ok) {
      throw new Error(`DASHBOARD_PAYMENTS_LIST_FAILED_${paymentsResponse.status}`);
    }

    if (!expensesResponse.ok) {
      throw new Error(`DASHBOARD_EXPENSES_LIST_FAILED_${expensesResponse.status}`);
    }

    const [paymentsPayload, expensesPayload] = await Promise.all([
      paymentsResponse.json(),
      expensesResponse.json(),
    ]);

    if (!Array.isArray(paymentsPayload)) {
      throw new Error('DASHBOARD_PAYMENTS_INVALID_PAYLOAD');
    }

    if (!Array.isArray(expensesPayload)) {
      throw new Error('DASHBOARD_EXPENSES_INVALID_PAYLOAD');
    }

    return {
      ok: true,
      data: {
        payments: paymentsPayload,
        expenses: expensesPayload,
      },
      error: null,
    };
  } catch (error) {
    return {
      ok: false,
      data: {
        payments: [],
        expenses: [],
      },
      error: error instanceof Error ? error.message : error,
    };
  }
}

export async function getDashboardDealsDataResult() {
  try {
    const [proposalsResponse, inProgressResponse, completedResponse] = await Promise.all([
      fetch(getDealsApiUrl(PROPOSALS_API_PATH), { headers: getDashboardAuthHeaders() }),
      fetch(getDealsApiUrl(IN_PROGRESS_API_PATH), { headers: getDashboardAuthHeaders() }),
      fetch(getDealsApiUrl(COMPLETED_API_PATH), { headers: getDashboardAuthHeaders() }),
    ]);

    await throwFirstDashboardFailure([proposalsResponse, inProgressResponse, completedResponse], {
      authError: 'DASHBOARD_DEALS_AUTH_REQUIRED',
      moduleDisabledError: 'DASHBOARD_DEALS_MODULE_DISABLED',
      tenantDatabasePrefix: 'DASHBOARD_DEALS',
    });

    if (!proposalsResponse.ok) {
      throw new Error(`DASHBOARD_PROPOSALS_LIST_FAILED_${proposalsResponse.status}`);
    }

    if (!inProgressResponse.ok) {
      throw new Error(`DASHBOARD_IN_PROGRESS_LIST_FAILED_${inProgressResponse.status}`);
    }

    if (!completedResponse.ok) {
      throw new Error(`DASHBOARD_COMPLETED_LIST_FAILED_${completedResponse.status}`);
    }

    const [proposalsPayload, inProgressPayload, completedPayload] = await Promise.all([
      proposalsResponse.json(),
      inProgressResponse.json(),
      completedResponse.json(),
    ]);

    if (!Array.isArray(proposalsPayload)) {
      throw new Error('DASHBOARD_PROPOSALS_INVALID_PAYLOAD');
    }

    if (!Array.isArray(inProgressPayload)) {
      throw new Error('DASHBOARD_IN_PROGRESS_INVALID_PAYLOAD');
    }

    if (!Array.isArray(completedPayload)) {
      throw new Error('DASHBOARD_COMPLETED_INVALID_PAYLOAD');
    }

    return {
      ok: true,
      data: {
        dealProposals: proposalsPayload,
        dealsInProgress: inProgressPayload,
        dealsCompleted: completedPayload,
      },
      error: null,
    };
  } catch (error) {
    return {
      ok: false,
      data: {
        dealProposals: [],
        dealsInProgress: [],
        dealsCompleted: [],
      },
      error: error instanceof Error ? error.message : error,
    };
  }
}

export async function getDashboardAssignmentsDataResult() {
  try {
    const [assignmentsResponse, staffResponse] = await Promise.all([
      fetch(getDealsApiUrl(ASSIGNMENTS_API_PATH), { headers: getDashboardAuthHeaders() }),
      fetch(getStaffApiUrl(STAFF_API_PATH), { headers: getDashboardAuthHeaders() }),
    ]);

    await throwFirstDashboardFailure([assignmentsResponse, staffResponse], {
      authError: 'DASHBOARD_ASSIGNMENTS_AUTH_REQUIRED',
      moduleDisabledError: 'DASHBOARD_ASSIGNMENTS_MODULE_DISABLED',
      tenantDatabasePrefix: 'DASHBOARD_ASSIGNMENTS',
    });

    if (!assignmentsResponse.ok) {
      throw new Error(`DASHBOARD_ASSIGNMENTS_LIST_FAILED_${assignmentsResponse.status}`);
    }

    if (!staffResponse.ok) {
      throw new Error(`DASHBOARD_STAFF_LIST_FAILED_${staffResponse.status}`);
    }

    const [assignmentsPayload, staffPayload] = await Promise.all([
      assignmentsResponse.json(),
      staffResponse.json(),
    ]);

    if (!Array.isArray(assignmentsPayload)) {
      throw new Error('DASHBOARD_ASSIGNMENTS_INVALID_PAYLOAD');
    }

    if (!Array.isArray(staffPayload)) {
      throw new Error('DASHBOARD_STAFF_INVALID_PAYLOAD');
    }

    return {
      ok: true,
      data: {
        assignments: assignmentsPayload.map(mapDashboardAssignment),
        staff: staffPayload.map(mapDashboardStaff),
      },
      error: null,
    };
  } catch (error) {
    return {
      ok: false,
      data: {
        assignments: [],
        staff: [],
      },
      error: error instanceof Error ? error.message : error,
    };
  }
}

export async function getDashboardClientsDataResult() {
  try {
    const clientsResponse = await fetch(getClientsApiUrl(CLIENTS_API_PATH), {
      headers: getDashboardAuthHeaders(),
    });

    await throwFirstDashboardFailure([clientsResponse], {
      authError: 'DASHBOARD_CLIENTS_AUTH_REQUIRED',
      moduleDisabledError: 'DASHBOARD_CLIENTS_MODULE_DISABLED',
      tenantDatabasePrefix: 'DASHBOARD_CLIENTS',
    });

    if (!clientsResponse.ok) {
      throw new Error(`DASHBOARD_CLIENTS_LIST_FAILED_${clientsResponse.status}`);
    }

    const clientsPayload = await clientsResponse.json();

    if (!Array.isArray(clientsPayload)) {
      throw new Error('DASHBOARD_CLIENTS_INVALID_PAYLOAD');
    }

    return {
      ok: true,
      data: {
        clients: clientsPayload.map(mapDashboardClient),
      },
      error: null,
    };
  } catch (error) {
    return {
      ok: false,
      data: {
        clients: [],
      },
      error: error instanceof Error ? error.message : error,
    };
  }
}
